import {
  compactSearchText,
  hasSearchPunctuation,
  splitSearchWords,
} from '~/chords/Select/components/DropdownSingleSelect/constants';
import { foldSearchText } from '~/chords/Select/components/DropdownSingleSelect/utils/foldSearchText';

export interface SearchTermWordCoverPart {
  wordIndex: number;
  length: number;
}

/**
 * Cover a compacted search term (e.g. "hipho") with label words in any order.
 * Each word is used at most once; the last word may be a prefix ("hipho" → Hip + Ho).
 * Full-word covers are preferred over prefixes of earlier words so "k" on
 * "Korea K" matches the standalone K rather than the K in Korea.
 *
 * Identical full words are interchangeable, so each distinct word is tried once
 * per remaining suffix. Failed (remaining, unusedCounts) states are memoized so
 * a miss like "aaa…ab" against many one-letter "a" words cannot explode into
 * n! permutations on every keystroke.
 */
export function findSearchTermWordCover(
  term: string,
  words: string[],
): SearchTermWordCoverPart[] | undefined {
  const compactedTerm = compactSearchText(term);
  const foldedWords = words.map(foldSearchText);

  if (!compactedTerm || foldedWords.length === 0) {
    return undefined;
  }

  const uniqueIndexByWord = new Map<string, number>();
  const unusedCounts: number[] = [];
  const uniqueIndexForWord = foldedWords.map((word) => {
    let uniqueIndex = uniqueIndexByWord.get(word);

    if (uniqueIndex === undefined) {
      uniqueIndex = unusedCounts.length;
      uniqueIndexByWord.set(word, uniqueIndex);
      unusedCounts.push(0);
    }

    unusedCounts[uniqueIndex] += 1;

    return uniqueIndex;
  });

  const used = foldedWords.map(() => false);
  const parts: SearchTermWordCoverPart[] = [];
  const failedStates = new Set<string>();

  const cover = (remainingStart: number): boolean => {
    if (remainingStart === compactedTerm.length) {
      return true;
    }

    const stateKey = `${remainingStart}:${unusedCounts.join(',')}`;

    if (failedStates.has(stateKey)) {
      return false;
    }

    const remaining = compactedTerm.slice(remainingStart);
    const triedFullWords = new Set<string>();

    for (let index = 0; index < foldedWords.length; index++) {
      if (used[index]) {
        continue;
      }

      const word = foldedWords[index];

      if (triedFullWords.has(word)) {
        continue;
      }

      triedFullWords.add(word);

      if (!remaining.startsWith(word)) {
        continue;
      }

      const uniqueIndex = uniqueIndexForWord[index];

      used[index] = true;
      unusedCounts[uniqueIndex] -= 1;
      parts.push({ wordIndex: index, length: word.length });

      if (cover(remainingStart + word.length)) {
        return true;
      }

      parts.pop();
      unusedCounts[uniqueIndex] += 1;
      used[index] = false;
    }

    for (let index = 0; index < foldedWords.length; index++) {
      if (used[index]) {
        continue;
      }

      const word = foldedWords[index];

      if (word.startsWith(remaining)) {
        parts.push({ wordIndex: index, length: remaining.length });

        return true;
      }
    }

    failedStates.add(stateKey);

    return false;
  };

  if (cover(0)) {
    return parts;
  }

  return undefined;
}

/**
 * True when a label contains all the words from a search term, in any order.
 * Matching uses accent-folded text; the label may contain diacritics ("Axé" → "axe").
 */
export function doesLabelMatchSearchTerm(label: string, term: string): boolean {
  const labelFolded = foldSearchText(label);
  const termFolded = foldSearchText(term);

  if (hasSearchPunctuation(termFolded) && labelFolded.includes(termFolded)) {
    return true;
  }

  return (
    findSearchTermWordCover(termFolded, splitSearchWords(labelFolded)) !==
    undefined
  );
}
